/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, type ChangeEvent } from 'react';
import { track } from '@fff-web/fff-analytics';
import { ICoupon, ICouponErrorResponse, IGiftCard } from '../../interfaces/coupon';
import { getCouponCodeData } from '../services/coupon';
import { getRedemptionErrorMessage } from '../utils/getRedemptionErrorMessage';
import { getCouponDiscount, getCouponType } from '../utils/segment';

export interface UseUniversalCouponInputProps {
  initialCouponCode?: string;
  plan: string;
  postalCode?: string;
  state?: string;
  country?: string;
}
const COUPON_ID = 'coupon';
const GIFT_CARD_ID = 'giftcard';
const CTA = 'Apply';
const SOURCE = 'Reactivation';
const DEFAULT_COUPON_ERROR_CODE = 'UNKNOWN';

const isCouponErrorResponse = (error: unknown): error is ICouponErrorResponse => {
  return !!error && typeof error === 'object' && 'message' in error && 'errorCode' in error;
};

const getCouponErrorResponse = (error: unknown): ICouponErrorResponse | null => {
  return isCouponErrorResponse(error) ? error : null;
};

const useUniversalCouponInput = ({
  initialCouponCode,
  plan,
  postalCode,
  state,
  country,
}: UseUniversalCouponInputProps) => {
  const [couponData, setCouponData] = useState<ICoupon | IGiftCard | null>(null);
  const [coupon, setCoupon] = useState<string>(initialCouponCode ?? '');
  const [error, setError] = useState<ICouponErrorResponse | null>(null);
  const [promoCodeType, setPromoCodeType] = useState<'coupon' | 'giftcard' | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleOnChange = (e?: ChangeEvent<HTMLInputElement>, couponCode?: string) => {
    const newCoupon = e ? e.target.value : couponCode || '';
    setCoupon(newCoupon);
    setError(null);
  };

  const handleStateBeforeFetch = () => {
    setError(null);
    setLoading(true);
    setCouponData(null);
    setPromoCodeType(null);
  };

  const handleCallCouponApi = async (couponCode?: string) => {
    handleStateBeforeFetch();
    const activeCoupon = couponCode || coupon;
    if (activeCoupon.length < 1) {
      setLoading(false);
      return;
    }
    if (couponCode) setCoupon(couponCode);

    track({
      trackName: 'Coupon Entered',
      properties: {
        coupon_name: activeCoupon,
        cta: CTA,
        source: SOURCE,
      },
    });

    try {
      const response = await getCouponCodeData({
        couponCode: activeCoupon,
        plan,
        postalCode,
        state,
        country,
      });

      const isCoupon = !!response.coupon;
      const responseData = isCoupon ? response.coupon : response.giftCard;
      setCouponData(responseData);
      setPromoCodeType(isCoupon ? COUPON_ID : GIFT_CARD_ID);

      if (responseData) {
        track({
          trackName: 'Coupon Applied',
          properties: {
            coupon_name: activeCoupon,
            coupon_type: getCouponType(responseData),
            discount: getCouponDiscount(responseData),
            cta: CTA,
            source: SOURCE,
            url: typeof window !== 'undefined' ? window.location.href : '',
          },
        });
      }
    } catch (error: unknown) {
      setCouponData(null);
      const couponError = getCouponErrorResponse(error);
      const errorCode = couponError?.errorCode ?? DEFAULT_COUPON_ERROR_CODE;
      const reason = couponError?.message?.trim() || getRedemptionErrorMessage(errorCode);

      setError({
        message: reason,
        errorCode,
      });

      track({
        trackName: 'Coupon Denied',
        properties: {
          coupon_name: activeCoupon,
          cta: CTA,
          source: SOURCE,
          reason,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (postalCode && postalCode.length > 4 && coupon) handleCallCouponApi();
  }, [postalCode]);

  useEffect(() => {
    if (coupon) handleCallCouponApi();
  }, [plan]);

  useEffect(() => {
    if (initialCouponCode) handleCallCouponApi();
  }, [initialCouponCode]);

  return {
    couponData,
    error,
    promoCodeType,
    handleCallCouponApi,
    loading,
    coupon,
    handleOnChange,
  };
};

export type UseUniversalCouponInputReturn = ReturnType<typeof useUniversalCouponInput>;
export default useUniversalCouponInput;
